import type { FocusableElement } from '@react-types/shared';
import type React from 'react';

import classNames from 'classnames';
import { useMemo, useState } from 'react';
import _ from 'underscore';

import { appConfig } from '../../../../appConfig';
import B2BCrossContextNotifications from '../B2BCrossContextNotifications';
import { QuantityStepper } from '../QuantityStepper';
import AddToCartA11yAnnouncer from './AddToCartA11yAnnouncer';

/**
 * Handles blocking of adding to cart when the user is in incorrect B2B context - private user on company site or company user on private site and shows a notification if it's the case.
 */
const AddToCartButton = (props: {
    className?: string;
    size?: 'default' | 'large';
    quantity: number;
    fullWidth?: boolean;
    isBlocked?: (newQuantity: number) => boolean;
    onBlock?: (newQuantity: number, prevQuantity: number) => void;
    withButton?: boolean;
    buyButtonText?: string;
    productName: string | undefined;
    getBuyButtonLabel?: (productName: string) => string;
    setQuantity: (newQuantity: number) => void;
    onAddToCartClick?: (e: React.MouseEvent<HTMLElement>) => void;
    onBuyButtonClick?: React.MouseEventHandler<FocusableElement>;
    onRemoveFromCartClick?: (e: React.MouseEvent<HTMLElement>) => void;
    disableAnnouncer?: boolean;
    autoFocus?: boolean;
}) => {
    const [attemptedToAddToCart, setAttemptedToAddToCart] = useState(false);

    const [a11yQuantity, setA11yQuantity] = useState<number | undefined>(undefined);
    const setA11yQuantityDebounced = useMemo(
        () => _.debounce(setA11yQuantity, 300),
        [setA11yQuantity],
    );

    const setQuantity = (newQuantity: number) => {
        setA11yQuantityDebounced(newQuantity); // announce to screen reader immediately after user interaction, dont wait for API sync to make sure AddToCartAccessibilityLogger is read before CartSummaryAccessibilityLogger
        props.setQuantity(newQuantity);
    };

    const onBlock = (newQuantity: number, prevQuantity: number) => {
        setAttemptedToAddToCart(true);
        props.onBlock?.(newQuantity, prevQuantity);
    };

    return (
        <div className={classNames('u-posRelative', props.className)}>
            {!props.disableAnnouncer && <AddToCartA11yAnnouncer quantity={a11yQuantity} />}

            {/* TODO remove renderTrigger and use plain refs to pass down to Modal */}
            <B2BCrossContextNotifications
                show={attemptedToAddToCart}
                close={() => setAttemptedToAddToCart(false)}
                renderTrigger={(addToCartRef, isCrossContextBlock) => (
                    <QuantityStepper
                        size={props.size}
                        withBuyButton={props.withButton}
                        buyButtonText={props.buyButtonText}
                        buyButtonRef={addToCartRef}
                        isBlocked={(newQuanity) =>
                            isCrossContextBlock || !!props.isBlocked?.(newQuanity)
                        }
                        onBlock={onBlock}
                        fullWidth={props.fullWidth}
                        quantity={props.quantity}
                        setQuantity={setQuantity}
                        onBuyButtonClick={props.onBuyButtonClick}
                        onAddClick={props.onAddToCartClick}
                        onRemoveClick={props.onRemoveFromCartClick}
                        buyButtonLabel={
                            (props.getBuyButtonLabel &&
                                props.productName &&
                                props.getBuyButtonLabel(props.productName)) ||
                            `Lägg till ${props.productName} i varukorgen`
                        }
                        inputLabel={`Antal av ${props.productName} i varukorgen`}
                        addButtonLabel={`Öka antal av ${props.productName} i varukorgen`}
                        removeButtonLabel={`Minska antal av ${props.productName} i varukorgen`}
                        autoFocus={props.autoFocus}
                    />
                )}
                continueAsPrivateUrl={appConfig.coopSettings.b2c.ecommerce.page.url}
                continueAsCompanyUrl={appConfig.coopSettings.b2b.ecommerce.page?.url}
            />
        </div>
    );
};

export default AddToCartButton;
